import type { ReactElement } from 'react';
import React from 'react';
import { toInteger } from 'lodash';
import { useParams } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';
import Loading from 'atom-components/loading';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import { isResponseReadyToBeMarked } from 'helpers/domain-specific/response-status.helpers';
import useMarkForMarking from 'hooks/marking/use-mark-for-marking';
import useResponseForMarking from 'hooks/marking/use-response-for-marking';
import useRubricForMarking from 'hooks/marking/use-rubric-for-marking';
import { useMe } from 'services/assist-api/entities/me/me';
import { MarkStatus } from 'services/assist-api/entities/test-instance-response-mark/types';
import { useTestInstanceResponseMarkers } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import { type TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { useTestInstance } from 'services/assist-api/entities/test-instance/test-instance';
import { isInitialLoad } from 'utils/check-query-status';
import CannotMarkMessages from './cannot-mark-messages';
import MarkResponseAuthoriser from './mark-response-authoriser';

type Props = {
  onSave: () => void;
};

/**
 * A reusable component that allows a marker to mark a test instance response. It can be embedded
 * into different outer mark response pages.
 */
export default function MarkResponse({ onSave }: Props): ReactElement {
  const { responseId } = useParams(); // Extracted from the slug
  const [queryParams] = useQueryParams({
    markId: StringParam
  });

  const testInstanceResponseMarkersQuery =
    useTestInstanceResponseMarkers<TestInstanceResponseMarkerWithCustomerUser>(
      {
        filters: { testInstanceResponse: responseId },
        groups: ['testinstanceresponsemarker-user'],
        include: 'users'
      },
      { enabled: !!responseId }
    );

  // Get the current user's details from the cache. We should have an earlier check to ensure that
  // the user has joined and is switched to an organisation by this point:
  const meQuery = useMe();

  // Get the saved mark if a mark ID is specified. New marks haven't been saved yet, so this query
  // should not prevent the form from loading.
  const testInstanceResponseMarkQuery = useMarkForMarking(queryParams.markId);

  const testInstanceResponseQuery = useResponseForMarking(toInteger(responseId));
  const testInstanceQuery = useTestInstance(testInstanceResponseQuery.data?.json.testInstance.id);
  const testRubricsQuery = useRubricForMarking(testInstanceQuery.data?.json.test.id);

  const queriesToLoad = [
    testInstanceQuery,
    testInstanceResponseQuery,
    testInstanceResponseMarkersQuery,
    testRubricsQuery,
    testInstanceResponseMarkQuery
  ];
  const isLoading =
    isInitialLoad(...queriesToLoad) ||
    !meQuery?.data?.customerUser ||
    !meQuery?.data?.customerRoles;

  const isError = queriesToLoad.some((q) => q.isError);

  const requiredMarkingData = {
    /**
     * Currently we are only fetching the rubric items for the first rubric. This is because for
     * CIPS Writing (currently the only test that will use the Marking Response page), there is
     * expected to only ever be only rubric per test.
     * If we need to mark tests with multiple rubrics, this logic will need to be expanded.
     */
    rubric: testRubricsQuery.data?.json[0],
    assessment: testInstanceQuery.data?.json,
    response: testInstanceResponseQuery.data?.json,
    currentOrganisationUser: meQuery.data?.customerUser
  };

  const responseReadyToBeMarked = isResponseReadyToBeMarked(
    testInstanceResponseQuery.data?.json.status
  );
  const mark = testInstanceResponseMarkQuery.data?.json;

  return (
    <>
      {isLoading && <Loading />}
      {!isLoading && isError && <LoadingErrorAlert queries={queriesToLoad} />}
      {!isLoading && !isError && (
        <>
          <CannotMarkMessages requiredMarkingData={requiredMarkingData} markStatus={mark?.status} />
          {requiredMarkingData.rubric &&
            requiredMarkingData.assessment &&
            requiredMarkingData.response &&
            requiredMarkingData.currentOrganisationUser &&
            (responseReadyToBeMarked || mark?.status === MarkStatus.ResponseReopened) && (
              <MarkResponseAuthoriser
                onSave={onSave}
                assessment={requiredMarkingData.assessment}
                response={requiredMarkingData.response}
                responseMarkers={testInstanceResponseMarkersQuery.data?.json || []}
                rubric={requiredMarkingData.rubric}
                currentOrganisationUser={requiredMarkingData.currentOrganisationUser}
                mark={mark}
              />
            )}
        </>
      )}
    </>
  );
}
